import React, { forwardRef, useState } from 'react';
import { Input as FiverInput, FormHelperText } from '@fiverr-private/forms';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { Box } from '@fiverr-private/theme';
import { EyeIcon, EyeSlashIcon } from '@fiverr-private/visuals';

interface FormInputProps {
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'search' | 'url' | 'date' | 'time';
  disabled?: boolean;
  autoComplete?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

  valid?: boolean;
  dirty?: boolean;
  pending?: boolean;
  reason?: string;
  errorMessage?: string;
  successMessage?: string;
  hint?: string;

  maxLength?: number;

  isRequired?: boolean;

  touched?: boolean;
  isEmpty?: () => boolean;
  setValue?: (value: string) => void;
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      value,
      defaultValue,
      placeholder,
      type = 'text',
      disabled = false,
      autoComplete,
      onChange,
      onFocus,
      onBlur,
      valid = true,
      dirty = false,
      pending = false,
      reason,
      errorMessage,
      successMessage,
      hint,

      maxLength,

      isRequired = false,

      touched: _touched,
      isEmpty: _isEmpty,
      setValue: _setValue,
      ...rest
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPasswordType = type === 'password';
    const inputType = isPasswordType && showPassword ? 'text' : type;

    const hasError = dirty && !pending && !valid;
    const hasSuccess = dirty && !pending && valid && !hasError;

    const displayErrorMessage = hasError ? reason || errorMessage : undefined;
    const displaySuccessMessage = hasSuccess ? successMessage : undefined;
    const displayHint = !hasError && !hasSuccess ? hint : undefined;

    const togglePasswordVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    return (
      <Container paddingY="2" position="relative">
        {isRequired && (
          <Typography color="red_700" position="absolute" top="4px" right="8px" zIndex="2">
            *
          </Typography>
        )}

        <Stack direction="row" alignItems="center" position="relative">
          <FiverInput
            ref={ref}
            value={value}
            defaultValue={defaultValue}
            placeholder={placeholder}
            type={inputType}
            disabled={disabled}
            autoComplete={autoComplete}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            isError={hasError}
            isSuccess={hasSuccess}
            maxLength={maxLength}
            {...rest}
          />
          {isPasswordType && (
            <Box
              position="absolute"
              right="12px"
              cursor="pointer"
              display="flex"
              alignItems="center"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <EyeSlashIcon size="md" /> : <EyeIcon size="md" />}
            </Box>
          )}
        </Stack>

        {(displayErrorMessage || displaySuccessMessage || displayHint) && (
          <Box fontSize="b_xs">
            <FormHelperText
              error={displayErrorMessage}
              success={displaySuccessMessage}
              hint={displayHint}
              isError={hasError}
              isSuccess={hasSuccess}
            />
          </Box>
        )}
      </Container>
    );
  }
);

FormInput.displayName = 'FormInput';
